import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {StripeError} from '@stripe/stripe-js';
import {useReservation} from 'context/reservation';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {
  ERROR_MODAL_ORIGIN,
  GUEST_APP_EVENTS_NAME,
  RESERVATION_PAYMENT_TYPES,
  SECURITY_DEPOSIT_STATUSES,
} from 'utils/constants';
import {useErrorModal, useModalControls} from 'utils/hooks';
import {usePaymentSettings} from 'hooks/usePaymentSettings';
import {getFinishCheckinHistoryArgs} from '../../utils/checkin';
import {PaymentFormRefType} from '../../components/PaymentForm/PaymentForm';
import {PaymentTypes} from 'hooks/usePayment';
import depositMade from 'assets/deposit-made-icon.png';
import PaymentForm from 'components/PaymentForm';
import PaidModal from 'components/PaidModal';
import {LearnMoreModal} from './components/LearnMoreModal';
import {
  Wrapper,
  Info,
  InfoDescription,
  InfoTitle,
  ButtonStyled,
  SecondaryButton,
} from './styled';
import {useDispatch} from 'react-redux';
import useMixPanel from 'hooks/useMixPanel';

const DEFAULT_DEPOSIT_AMOUNT = '0.00';

function getSecurityDeposit(reservation?: any) {
  if (!reservation) {
    return DEFAULT_DEPOSIT_AMOUNT;
  }

  return reservation?.security_deposit_amount || DEFAULT_DEPOSIT_AMOUNT;
}

export function getIsDepositMandatory(reservation?: any) {
  if (!reservation) {
    return false;
  }

  return (
    reservation?.housing?.security_deposit_status === SECURITY_DEPOSIT_STATUSES.mandatory
  );
}

const ProtectForm = React.memo(() => {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const {ErrorModal, displayError} = useErrorModal();
  const {data: reservation} = useReservation();
  const reservationDetails = useComputedReservationDetails();
  const paymentFormRef = React.useRef<PaymentFormRefType>();
  const {
    paymentSettingsCurrencySign,
    arePaymentSettingsValid,
    isLoading: isLoadingPaymentSettings,
  } = usePaymentSettings();
  const {
    isOpen: isPaidModalOpen,
    closeModal: closePaidModal,
    openModal: openPaidModal,
  } = useModalControls(false);
  const {
    isOpen: isLearnMoreModalOpen,
    closeModal: closeLearnMoreModal,
    openModal: openLearnMoreModal,
  } = useModalControls();
  const dispatch = useDispatch();
  const {PaymentEvents} = useMixPanel();

  React.useEffect(() => {
    if (reservation && !reservation.security_deposit) {
      PaymentEvents(GUEST_APP_EVENTS_NAME.paymentRequest, {
        type: RESERVATION_PAYMENT_TYPES.segurityDeposit,
      });
    }
  }, [dispatch, PaymentEvents, reservation]);

  const onPaymentError = (error?: StripeError) => {
    const errorModalorigin = ERROR_MODAL_ORIGIN.stripe;
    displayError(error, errorModalorigin);
  }; 

  const isDepositMandatory = getIsDepositMandatory(reservation);
  const depositAmount = getSecurityDeposit(reservation);

  React.useEffect(() => {
    const hasReservation = Boolean(reservation?.id);
    const isPaymentLoading = paymentFormRef.current?.isLoading;
    const shouldRedirect =
      (hasReservation &&
        !reservationDetails.hasDeposits &&
        !isPaymentLoading &&
        !isLoadingPaymentSettings) ||
      !arePaymentSettingsValid;

    if (shouldRedirect) {
      history.push('/finish');
    }
  }, [
    arePaymentSettingsValid,
    reservationDetails.hasDeposits,
    history,
    reservation,
    isLoadingPaymentSettings,
  ]);

  const gotTo = React.useCallback(() => {
    const {url, state} = getFinishCheckinHistoryArgs({
      reservationDetails,
      locationState: location.state,
      skip: {hasDeposit: true},
    });
    history.push(url, state);
  }, [history, location.state, reservationDetails]);

  const handlePaymentSuccess = React.useCallback(() => {
    closePaidModal();
    gotTo();
  }, [closePaidModal, gotTo]);

  return (
    <Wrapper>
      <div>
        <Info>
          <InfoTitle>{t('security_deposit')}</InfoTitle>
          <br />
          <InfoDescription>
            <Trans i18nKey="block_deposit_amount">
              According to the deposit policy, we will block the listed deposit amount
              from a credit card. The deposit <b>will be returned</b> after the end of
              your stay if no damage occurs.
            </Trans>
          </InfoDescription>
          <ButtonStyled
            onClick={openLearnMoreModal}
            secondary
            label={t('learn_more')}
          ></ButtonStyled>
          <br />
          <InfoDescription>
            <Trans
              i18nKey="amount_will_be_blocked_from_card"
              tOptions={{
                amount: depositAmount,
                currency_sign: paymentSettingsCurrencySign,
              }}
            >
              <b>
                {depositAmount}
                {paymentSettingsCurrencySign}
              </b>{' '}
              will be blocked from your credit card
            </Trans>
          </InfoDescription>
        </Info>
      </div>
      <div>
        <PaymentForm
          ref={paymentFormRef}
          paymentType={PaymentTypes.security_deposit}
          onPaymentSuccess={openPaidModal}
          onPaymentError={onPaymentError}
        />
        {!isDepositMandatory && (
          <SecondaryButton
            secondary
            label={t('pay_at_the_accommodation')}
            onClick={gotTo}
          />
        )}
      </div>
      <PaidModal
        imageSrc={depositMade}
        totalPice={depositAmount}
        subTitle={t('your_deposit_has_been_made')}
        isOpenPaidModal={isPaidModalOpen}
        isPaymentMethod={true}
        handlePaidModal={handlePaymentSuccess}
      />
      <LearnMoreModal
        isOpenPaidModal={isLearnMoreModalOpen}
        handleCloseModal={closeLearnMoreModal}
      />
      <ErrorModal />
    </Wrapper>
  );
});

export {ProtectForm};
